import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ORDER_STATUS_PAGE_ACTIONS } from '../actions/order-status.actions';
import { switchMap, take, map, tap, withLatestFrom } from 'rxjs/operators';
import { UserActions } from '@app/user/store/actions/action-types';
import { of } from 'rxjs';
import {
  OrderStatusFilters,
  UserPreferencesV2,
} from '@usf/user-types/user-preferencev2';
import { UserService } from 'src/app/user/services/user.service';
import { MyOrdersStoreService } from '@usf/ngrx-order';
import { Store } from '@ngrx/store';
import { ordersFiltersSelectors } from '@usf/ngrx-order';
import { DatePresetService } from '../../services/date-preset.service';
import { DatePreset } from '../../constants/date-preset.enum';

@Injectable()
export class OrderStatusEffects {
  constructor(
    private actions$: Actions,
    private userService: UserService,
    private myOrdersStoreService: MyOrdersStoreService,
    private store: Store,
  ) {}

  postOrderStatusFilterPreferences$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ORDER_STATUS_PAGE_ACTIONS.postOrderStatusFilterPreferences),
      switchMap(action => {
        const { orderStatusFilters, rememberSelections } = action;

        // Create a new filters object that only includes the filters that should be remembered
        const filtersToRemember: Partial<OrderStatusFilters['filters']> = {};

        if (rememberSelections?.customers) {
          filtersToRemember.customers = orderStatusFilters.filters.customers;
        }

        if (rememberSelections?.deliveryMethod) {
          filtersToRemember.deliveryMethod =
            orderStatusFilters.filters.deliveryMethod;
        }

        if (rememberSelections?.orderStatus) {
          filtersToRemember.orderStatus =
            orderStatusFilters.filters.orderStatus;
        }

        if (rememberSelections?.deliveryDatePreset) {
          filtersToRemember.deliveryDatePreset = (
            orderStatusFilters.filters as any
          ).deliveryDate?.preset;
        }

        const optionsSelected = this.countOptionsSelected(filtersToRemember);

        return this.userService.getUserPreferencesV2().pipe(
          take(1),
          switchMap(userPrefs => {
            // Only include filters that should be remembered in the preferences
            const preferences: UserPreferencesV2 = {
              userId: userPrefs?.userId,
              favoriteCustomers: userPrefs?.favoriteCustomers,
              orderStatusFilters: {
                filterSelected: optionsSelected,
                filters: filtersToRemember,
              },
            };

            return of(
              UserActions.updateUserPreferencesV2({
                preferences,
              }),
            );
          }),
        );
      }),
    ),
  );

  setOrderStatusFiltersByPreferences$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.getUserPreferencesV2Success),
        switchMap(action => {
          return this.store
            .select(ordersFiltersSelectors.selectOrdersFilters)
            .pipe(
              take(1),
              map(existingFilters => {
                const filtersHaveValues =
                  existingFilters?.filterSelected ||
                  Object.keys(existingFilters?.filters || {}).length > 0;

                // Get the preference filters
                const preferenceFilters =
                  action.preferences.orderStatusFilters?.filters || {};

                // Start with a copy of the existing filters object structure
                const result = {
                  // Keep the existing filterSelected property
                  filterSelected: existingFilters.filterSelected || 0,
                  // Initialize with the existing filters object
                  filters: { ...((existingFilters.filters || {}) as any) },
                };

                // Only override specific filter categories if they exist in preferences
                if (preferenceFilters.customers) {
                  result.filters.customers = preferenceFilters.customers;
                }

                if (preferenceFilters.deliveryMethod) {
                  result.filters.deliveryMethod =
                    preferenceFilters.deliveryMethod;
                }

                if (preferenceFilters.orderStatus) {
                  result.filters.orderStatus = preferenceFilters.orderStatus;
                }

                // If deliveryDatePreset exists in preferences, calculate and set start/end dates
                if (preferenceFilters.deliveryDatePreset) {
                  const preset =
                    preferenceFilters.deliveryDatePreset as DatePreset;
                  const dateRange = DatePresetService.getDateRange(preset);

                  // Initialize deliveryDate if it doesn't exist
                  if (!result.filters.deliveryDate) {
                    result.filters.deliveryDate = {};
                  }

                  // Format dates as strings (M/D/YYYY)
                  const formatDate = (date: Date) => {
                    return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
                  };

                  // Set the start and end dates in the deliveryDate filter
                  result.filters.deliveryDate.start = formatDate(
                    dateRange.from,
                  );
                  result.filters.deliveryDate.end = formatDate(dateRange.to);
                  result.filters.deliveryDate.preset = preset;
                }

                // Calculate the number of selected options
                const numberOfSelectedFilters =
                  this.countOptionsSelected(preferenceFilters);

                return {
                  filters: result.filters,
                  numberOfSelectedFilters,
                  filtersHaveValues,
                };
              }),
              tap(({ filters, numberOfSelectedFilters, filtersHaveValues }) => {
                if (!filtersHaveValues) {
                  this.myOrdersStoreService.setMyOrdersFilters(
                    filters,
                    numberOfSelectedFilters,
                  );
                }
              }),
            );
        }),
      ),
    { dispatch: false },
  );

  private countOptionsSelected(
    filters: Partial<OrderStatusFilters['filters']>,
  ): number {
    let count = 0;

    // Count selected customers
    if (filters?.customers) {
      Object.values(filters.customers).forEach(value => {
        if (typeof value === 'boolean' && value) {
          count++;
        } else if (typeof value === 'object') {
          count += Object.values(value).filter(v => v).length;
        }
      });
    }

    // Count selected delivery methods
    if (filters?.deliveryMethod) {
      count += Object.values(filters.deliveryMethod).filter(v => v).length;
    }

    // Count selected order statuses
    if (filters?.orderStatus) {
      count += Object.values(filters.orderStatus).filter(v => v).length;
    }
    if (filters?.deliveryDatePreset) {
      count++;
    }

    return count;
  }
}
